import {Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import * as Yup from 'yup'
import {safeText} from 'src/cms/helpers/validation/safeText'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import * as tisaForexRateCategoryRedux from 'src/app/modules/rates/tisaForexRateCategory/redux/index'

type Props = {
  open: boolean
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const FORM_VALIDATION = Yup.object().shape({
  name: safeText().required('Name is required'),
  code: Yup.string().required('Code is required'),
  order: Yup.number().required('Order is required').min(1, 'Order must be at least 1'),
  currency_unit: Yup.number().required('Currency unit is required').min(1, 'Currency unit must be at least 1'),
})

const AddTisaForexRateCategory = ({
  open,
  handleClose,
  actionType,
  editSelectedData,
}: Props) => {
  const dispatch = useDispatch()
  const {loading, success} = useSelector((state: any) => state.tisaForexRateCategory)

  useEffect(() => {
    if (success) {
      dispatch(tisaForexRateCategoryRedux.actions.getAllTisaForexRateCategory())
      isEmpty(editSelectedData)
        ? toast.success('Tisa Forex Rate Category added successfully')
        : toast.success('Tisa Forex Rate Category edited successfully')
      dispatch(tisaForexRateCategoryRedux.actions.addTisaForexRateCategoryReset())
      handleClose()
    }
  }, [success])

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} Tisa Forex Rate Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={{
                name: '',
                code: '',
                order: '',
                currency_unit: '',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => {
                const formData = {
                  name: values.name,
                  code: values.code,
                  order: Number(values.order),
                  currency_unit: Number(values.currency_unit),
                }
                if (!isEmpty(editSelectedData)) {
                  dispatch(
                    tisaForexRateCategoryRedux.actions.updateTisaForexRateCategory(
                      formData,
                      editSelectedData?.id
                    )
                  )
                } else {
                  dispatch(tisaForexRateCategoryRedux.actions.addTisaForexRateCategory(formData))
                }
              }}
            >
              {({touched, handleChange, errors, setFieldValue}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    setFieldValue('name', editSelectedData?.name ?? '', false)
                    setFieldValue('code', editSelectedData?.code ?? '', false)
                    setFieldValue('order', editSelectedData?.order ?? '', false)
                    setFieldValue('currency_unit', editSelectedData?.currency_unit ?? '', false)
                  }
                }, [])

                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Name'
                            label='Name'
                            name='name'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            data-cy='tisa-forex-rate-category-name'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Code (e.g. USD)'
                            label='Code'
                            name='code'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            data-cy='tisa-forex-rate-category-code'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Order'
                            label='Order'
                            name='order'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            data-cy='tisa-forex-rate-category-order'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Currency Unit'
                            label='Currency Unit'
                            name='currency_unit'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            data-cy='tisa-forex-rate-category-currency-unit'
                          />
                        </div>

                      </div>
                    </div>

                    <div className='d-flex justify-content-end px-9'>
                      <button
                        type='submit'
                        disabled={loading}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        Save
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AddTisaForexRateCategory
